import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import moduleRealtimeService from '../../../services/moduleRealtimeService';

const ProjectProgressOverview = ({ data, modules = [], onProgressUpdate }) => {
  const { projectId } = useParams();

  // Real-time subscription for progress changes
  React.useEffect(() => {
    if (!projectId) return;

    const progressChannel = moduleRealtimeService?.subscribeToProgressChanges(
      projectId,
      (payload) => {
        console.log('Progress change detected:', payload?.event);
        
        // Trigger progress refresh when data changes
        if (onProgressUpdate) {
          onProgressUpdate();
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      moduleRealtimeService?.unsubscribe(progressChannel);
    };
  }, [projectId, onProgressUpdate]);

  // Calculate In Pipeline from total module quantities
  const totalModuleQuantity = modules?.reduce((sum, module) => sum + (module?.quantity || 0), 0);

  if (!data) {
    return (
      <div className="bg-card rounded-xl border border-border p-3 min-w-0">
        <h2 className="text-sm font-semibold text-foreground mb-3 truncate">Module Progress Overview</h2>
        <p className="text-muted-foreground text-xs">No progress data available</p>
      </div>
    );
  }

  const progressItems = [
    { label: 'In Pipeline', value: totalModuleQuantity, icon: 'Inbox', color: '#8B5CF6' },
    { label: 'Started', value: data?.started || 0, icon: 'Play', color: '#3B82F6' },
    { label: 'On the Floor', value: data?.onFloor || 0, icon: 'Activity', color: '#F59E0B' },
    { label: 'Completed', value: data?.completed || 0, icon: 'CheckCircle', color: '#10B981' },
    { label: 'Delivered', value: data?.delivered || 0, icon: 'Package', color: '#06B6D4' },
    { label: 'Delayed', value: data?.delayed || 0, icon: 'AlertTriangle', color: '#EF4444' }
  ];

  // Prepare data for pie chart
  const pieChartData = progressItems?.filter(item => item?.value > 0)?.map(item => ({
    name: item?.label,
    value: item?.value,
    color: item?.color
  }));

  // Custom label renderer for pie chart
  const renderLabel = (entry) => {
    const total = progressItems?.reduce((sum, item) => sum + item?.value, 0);
    const percent = total > 0 ? Math.round((entry?.value / total) * 100) : 0;
    return `${percent}%`;
  };

  // Custom label style with 40% reduced font size
  const labelStyle = {
    fontSize: '9.6px', // 40% reduction from default 16px
    fontWeight: 600
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0];
      const total = progressItems?.reduce((sum, item) => sum + item?.value, 0);
      const percent = total > 0 ? Math.round((data?.value / total) * 100) : 0;
      return (
        <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
          <p className="text-sm font-semibold text-foreground">{data?.name}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {data?.value} ({percent}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-3 h-full min-w-0">
      <h2 className="text-sm font-semibold text-foreground mb-3 truncate">Module Progress Overview</h2>
      <div className="flex gap-3">
        {/* List - moved to left */}
        <div className="flex-shrink-0 w-32 flex flex-col" style={{ height: '100%', minHeight: '250px', maxHeight: '350px' }}>
          <div className="flex flex-col justify-start gap-3 pt-8">
            {progressItems?.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${item?.color}20` }}
                >
                  <Icon name={item?.icon} size={14} color={item?.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-1">
                    <span className="text-xs text-muted-foreground truncate">{item?.label}</span>
                    <span className="text-xs font-semibold text-foreground">{item?.value}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pie Chart - Responsive */}
        <div className="flex-1 min-w-0">
          {pieChartData?.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" minHeight={250} maxHeight={350}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="23%"
                  outerRadius="47%"
                  paddingAngle={2}
                  dataKey="value"
                  label={renderLabel}
                  labelLine={false}
                  style={labelStyle}
                >
                  {pieChartData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full min-h-[250px] max-h-[350px] flex items-center justify-center text-xs text-muted-foreground">
              No data
            </div>
          )}
        </div>
      </div>
      {data?.delayReasons && (
        <div className="mt-3 p-2 bg-muted rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={12} color="var(--color-muted-foreground)" className="mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground mb-0.5">Delay Reasons:</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{data?.delayReasons}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectProgressOverview;